import { TranslationLocales } from './';

type ITranslationFunction = {
  [key in BaseTranslationKeys]: ({}?: { [arg: string]: string|number }) => string;
}

export type BaseTranslationKeys =
'adultNum'|
'childNum'|
'studentNum'|
'seniorNum'|
'peopleNum'|
'addToCart'|
'purchase'|
'seeCart'|
'close'|
'checkout'|
'cartIsEmpty'|
'dateOfUse'|
'startDate'|
'icCardDeposit'|
'itemIdIsRequired'|
'pleaseSelectNumberOfTicketsAndDateOfUse'|
'pleaseSelectNumberOfPeopleAndDateOfUse'|
'pleaseSelectNumberOfPeopleAndDateDuration'|
'pleaseSelectNumberOfMalesAndFemales'|
'pleaseSelectValidNumberOfParticipants'|
'pleaseSelectPickupPlace'|
'pleaseNoteCancellationsCannotBeMadeAfter180Days'|
'areYouSureToRemoveThisItemFromCart'|
'itemIsAddedToCart'|
'paymentIsInJapaneseYen'|
'addAccommodation'|
'addLiftTicket'|
'addLiftTicketAndRental'|
'addTransport'|
'addInsurance'|
'useCoupon'|
'changeCoupon'|
'couponIsSelected'|
'chooseATime'|
'discountAmountWillBeCalculatedInTheNextPage'|
'cannotAddToShoppingCart';

export const baseTranslations: {
  [locale in TranslationLocales]: ITranslationFunction
} = {
  'en': {
    adultNum: () => ('Adult'),
    childNum: () => ('Child'),
    studentNum: () => ('Student'),
    seniorNum: () => ('Senior'),
    peopleNum: () => ('Person/People'),
    addToCart: () => ('Add to cart'),
    purchase: () => ('Buy'),
    seeCart: () => ('View Cart'),
    close: () => ('Close'),
    checkout: () => ('Proceed to Checkout'),
    cartIsEmpty: () => ('Cart is Empty'),
    dateOfUse: () => ('Date of Use'),
    startDate: () => ('Start Date'),
    icCardDeposit: () => ('IC card deposit'),
    itemIdIsRequired: () => ('Item ID is required.'),
    pleaseSelectNumberOfTicketsAndDateOfUse: () => ('Please select amount and date.'),
    pleaseSelectNumberOfPeopleAndDateOfUse: () => ('Please select the number of travelers and the date(s).'),
    pleaseSelectNumberOfPeopleAndDateDuration: () => ('Please select the number of people and day(s).'),
    pleaseSelectNumberOfMalesAndFemales: () => ('Please select number of male and female.'),
    pleaseSelectValidNumberOfParticipants: ({ max }: { max: number }) => (
      `The total number of people must not exceed ${max} people.`),
    pleaseSelectPickupPlace: () => ('Please Select a Pickup Location.'),
    pleaseNoteCancellationsCannotBeMadeAfter180Days: () => (
      '*Please note that cancellations cannot be made after 180 days from the booking confirmation date.'),
    areYouSureToRemoveThisItemFromCart: () => ('Are you sure you would like to remove this item from the cart?'),
    itemIsAddedToCart: () => ('The item has been successfully added to your cart.'),
    paymentIsInJapaneseYen: () => (
      'Payment shall be made in Japanese Yen. Displayed prices other than Japanese Yen are based on the current exchange rate.'),
    addAccommodation: () => ('Add Accommodation'),
    addLiftTicket: () => ('Add Lift Pass'),
    addLiftTicketAndRental: () => ('Add Lift Pass/Rental'),
    addTransport: () => ('Add Transport'),
    addInsurance: () => ('Add Insurance'),
    useCoupon: () => ('Use a coupon'),
    changeCoupon: () => ('Change a coupon'),
    couponIsSelected: () => ('Coupon is selected.'),
    chooseATime: () => ('Please choose a time'),
    discountAmountWillBeCalculatedInTheNextPage: () => ('Discount calculated on next page.'),
    cannotAddToShoppingCart: () => ('We are not able to put your item into shopping cart, please check your internet connection.')
  },
  'ja': {
    adultNum: () => ('大人'),
    childNum: () => ('子供'),
    studentNum: () => ('学生'),
    seniorNum: () => ('シニア'),
    peopleNum: () => ('人数'),
    addToCart: () => ('カートに追加'),
    purchase: () => ('購入する'),
    seeCart: () => ('カートを確認'),
    close: () => ('閉じる'),
    checkout: () => ('購入に進む'),
    cartIsEmpty: () => ('カートは空です'),
    dateOfUse: () => ('利用日'),
    startDate: () => ('利用開始日'),
    icCardDeposit: () => ('ICカード保証金'),
    itemIdIsRequired: () => ('商品IDは必須です。'),
    pleaseSelectNumberOfTicketsAndDateOfUse: () => ('枚数と日付を選択してください。'),
    pleaseSelectNumberOfPeopleAndDateOfUse: () => ('人数と日付を選択してください。'),
    pleaseSelectNumberOfPeopleAndDateDuration: () => ('人数と日数を選択してください。'),
    pleaseSelectNumberOfMalesAndFemales: () => ('男性と女性の人数を選択してください。'),
    pleaseSelectValidNumberOfParticipants: ({ max }: { max: number }) => (`合計で${max}人以内になるように人数を選択してください。`),
    pleaseSelectPickupPlace: () => ('チケットの受け取り場所を選択してください'),
    pleaseNoteCancellationsCannotBeMadeAfter180Days: () => ('※予約確定日から180日以降はキャンセルできませんのでご注意ください。'),
    areYouSureToRemoveThisItemFromCart: () => ('この商品をカートから削除しますか？'),
    itemIsAddedToCart: () => ('商品がカートに追加されました。'),
    paymentIsInJapaneseYen: () => ('決済は日本円で行われます。'),
    addAccommodation: () => ('宿泊を追加'),
    addLiftTicket: () => ('リフト券を追加'),
    addLiftTicketAndRental: () => ('リフト券&レンタルを追加'),
    addTransport: () => ('交通手配を追加'),
    addInsurance: () => ('保険を追加'),
    useCoupon: () => ('割引クーポンを使用する'),
    changeCoupon: () => ('割引クーポンを変更する'),
    couponIsSelected: () => ('割引クーポンが選択されています。'),
    chooseATime: () => ('時間を選択してください'),
    discountAmountWillBeCalculatedInTheNextPage: () => ('割引額は次のページで計算されます。'),
    cannotAddToShoppingCart: () => ('カートに追加できませんでした。安定した通信環境で再度お試しください。')
  },
  'zh-CN': {
    adultNum: () => ('成人'),
    childNum: () => ('儿童'),
    studentNum: () => ('学生'),
    seniorNum: () => ('老年人'),
    peopleNum: () => ('人数'),
    addToCart: () => ('加进购物车'),
    purchase: () => ('购买'),
    seeCart: () => ('确认购物车'),
    close: () => ('关闭'),
    checkout: () => ('前往付款'),
    cartIsEmpty: () => ('购物车内没有商品'),
    dateOfUse: () => ('使用日期'),
    startDate: () => ('开始使用日'),
    icCardDeposit: () => ('电子卡押金'),
    itemIdIsRequired: () => ('请输入商品ID。'),
    pleaseSelectNumberOfTicketsAndDateOfUse: () => ('请选择张数及日期。'),
    pleaseSelectNumberOfPeopleAndDateOfUse: () => ('请选择人数及日期。'),
    pleaseSelectNumberOfPeopleAndDateDuration: () => ('请选择人数和天数。'),
    pleaseSelectNumberOfMalesAndFemales: () => ('请选择男性和女性的人数。'),
    pleaseSelectValidNumberOfParticipants: ({ max }: { max: number }) => (`请选择总和${max}位以内的人数。`),
    pleaseSelectPickupPlace: () => ('请选择取票地点。'),
    pleaseNoteCancellationsCannotBeMadeAfter180Days: () => ('※预约完成日起算180天后将无法取消，敬请留意。'),
    areYouSureToRemoveThisItemFromCart: () => ('确定要删除此商品吗？'),
    itemIsAddedToCart: () => ('商品已加入购物车。'),
    paymentIsInJapaneseYen: () => ('订单将以日元结算。日元以外的显示价格是，根据搜索时的汇率计算出的参考价格。'),
    addAccommodation: () => ('追加住宿'),
    addLiftTicket: () => ('追加缆车券'),
    addLiftTicketAndRental: () => ('追加缆车券和租借服务'),
    addTransport: () => ('追加交通服务'),
    addInsurance: () => ('追加保险'),
    useCoupon: () => ('使用优惠券'),
    changeCoupon: () => ('变更优惠券'),
    couponIsSelected: () => ('已选择优惠券。'),
    chooseATime: () => ('请选择时间'),
    discountAmountWillBeCalculatedInTheNextPage: () => ('折扣后的价格将于下个页面显示。'),
    cannotAddToShoppingCart: () => ('对不起，这商品没有被放进购物车，请确认阁下在使用稳定的网络。')
  },
  'zh-HK': {
    adultNum: () => ('成人'),
    childNum: () => ('小童'),
    studentNum: () => ('學生'),
    seniorNum: () => ('長者'),
    peopleNum: () => ('人數'),
    addToCart: () => ('加入購物車'),
    purchase: () => ('購買'),
    seeCart: () => ('確認購物車'),
    close: () => ('關閉'),
    checkout: () => ('進入購買'),
    cartIsEmpty: () => ('購物車沒有商品'),
    dateOfUse: () => ('使用日期'),
    startDate: () => ('開始使用日'),
    icCardDeposit: () => ('電子卡按金'),
    itemIdIsRequired: () => ('必須有商品ID。'),
    pleaseSelectNumberOfTicketsAndDateOfUse: () => ('請選擇張數及日期。'),
    pleaseSelectNumberOfPeopleAndDateOfUse: () => ('請選擇人數及日期。'),
    pleaseSelectNumberOfPeopleAndDateDuration: () => ('請選擇人數和日數。'),
    pleaseSelectNumberOfMalesAndFemales: () => ('請選擇男性與女性的人數。'),
    pleaseSelectValidNumberOfParticipants: ({ max }: { max: number }) => (`請選擇合共${max}位以內的人數。`),
    pleaseSelectPickupPlace: () => ('請選擇取票地點'),
    pleaseNoteCancellationsCannotBeMadeAfter180Days: () => ('※預約完成日起計180日後將無法取消，敬請留意。'),
    areYouSureToRemoveThisItemFromCart: () => ('刪除此商品？'),
    itemIsAddedToCart: () => ('商品已加入購物車。'),
    paymentIsInJapaneseYen: () => ('以日圓付款。日元以外的顯示價格是，根據搜尋時的匯率計算出的參考價格。'),
    addAccommodation: () => ('添購住宿'),
    addLiftTicket: () => ('添購纜車票'),
    addLiftTicketAndRental: () => ('添購纜車票&租借服務'),
    addTransport: () => ('添購交通服務'),
    addInsurance: () => ('添購保險'),
    useCoupon: () => ('使用折扣券'),
    changeCoupon: () => ('變更折扣券'),
    couponIsSelected: () => ('已選擇折扣券。'),
    chooseATime: () => ('請選擇時間'),
    discountAmountWillBeCalculatedInTheNextPage: () => ('折扣後的價格會在下一頁顯示。'),
    cannotAddToShoppingCart: () => ('對不起，這商品未能放進購物車，請確保閣下在使用穩定的網絡。')
  },
  'zh-TW': {
    adultNum: () => ('成人'),
    childNum: () => ('兒童'),
    studentNum: () => ('學生'),
    seniorNum: () => ('年長者'),
    peopleNum: () => ('人數'),
    addToCart: () => ('加入購物車'),
    purchase: () => ('購買'),
    seeCart: () => ('檢視購物車'),
    close: () => ('關閉'),
    checkout: () => ('前往結帳'),
    cartIsEmpty: () => ('購物車內沒有商品'),
    dateOfUse: () => ('使用日期'),
    startDate: () => ('開始使用日'),
    icCardDeposit: () => ('IC卡押金'),
    itemIdIsRequired: () => ('請輸入商品ID。'),
    pleaseSelectNumberOfTicketsAndDateOfUse: () => ('請選擇張數及日期。'),
    pleaseSelectNumberOfPeopleAndDateOfUse: () => ('請選擇人數及日期。'),
    pleaseSelectNumberOfPeopleAndDateDuration: () => ('請選擇人數和天數。'),
    pleaseSelectNumberOfMalesAndFemales: () => ('請選擇男性女姓的人數。'),
    pleaseSelectValidNumberOfParticipants: ({ max }: { max: number }) => (`請選擇總和${max}位以內的人數。`),
    pleaseSelectPickupPlace: () => ('請選擇取票地點'),
    pleaseNoteCancellationsCannotBeMadeAfter180Days: () => ('※從完成預約當日起計180天後將無法取消，敬請留意。'),
    areYouSureToRemoveThisItemFromCart: () => ('確定要刪除此商品嗎？'),
    itemIsAddedToCart: () => ('商品已放入購物車。'),
    paymentIsInJapaneseYen: () => ('訂單將以日圓結帳。日元以外的顯示價格是，根據搜尋時的匯率計算出的參考價格。'),
    addAccommodation: () => ('追加住宿'),
    addLiftTicket: () => ('追加纜車券'),
    addLiftTicketAndRental: () => ('追加纜車券與租借服務'),
    addTransport: () => ('追加交通服務'),
    addInsurance: () => ('追加保險'),
    useCoupon: () => ('使用優惠券'),
    changeCoupon: () => ('變更優惠券'),
    couponIsSelected: () => ('已選擇優惠券。'),
    chooseATime: () => ('請選擇時間'),
    discountAmountWillBeCalculatedInTheNextPage: () => ('折扣後的價格將於下個頁面顯示。'),
    cannotAddToShoppingCart: () => ('對不起，這商品沒有被放進購物車，請確認閣下在使用穩定的網絡。')
  },
};
